import React, { useCallback, useState } from 'react';
import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import InfoIcon from '@atlaskit/icon/core/information';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import Popup from '@atlaskit/popup';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import messages from './messages';

type EmbedCodeProps = {
	embedCode: string;
};

export const EmbedCode = ({ embedCode }: EmbedCodeProps) => {
	const { formatMessage } = useIntl();
	const [isPopupOpen, setIsPopupOpen] = useState(false);

	const handleCopyClick = useCallback(() => {
		navigator.clipboard.writeText(embedCode);
		setIsPopupOpen(true);
		setTimeout(() => setIsPopupOpen(false), 2000);
	}, [embedCode, setIsPopupOpen]);

	const closePopup = useCallback(() => setIsPopupOpen(false), [setIsPopupOpen]);

	if (embedCode === undefined || embedCode === '') {
		return (
			<Stack space="space.100">
				<Text as="p">{formatMessage(messages.helpText)}</Text>
				{EmptyEmbedCode()}
			</Stack>
		);
	}
	return (
		<Stack space="space.100">
			<Text as="p">{formatMessage(messages.helpText)}</Text>
			<Box
				xcss={emdedCodeStyles}
				testId="servicedesk-widget-settings.common.embed-code-v2.embed-code-v-2"
				as="p"
			>
				{embedCode}
			</Box>
			<Box xcss={buttonStyles}>
				<Popup
					isOpen={isPopupOpen}
					onClose={closePopup}
					placement="right"
					content={() => (
						<Box xcss={popupStyles}>{formatMessage(messages.codeCopiedConfirmationText)}</Box>
					)}
					trigger={(triggerProps) => (
						<Button
							iconBefore={CopyIcon}
							appearance="default"
							{...triggerProps}
							onClick={handleCopyClick}
						>
							{formatMessage(messages.copyCodeButtonText)}
						</Button>
					)}
				/>
			</Box>
		</Stack>
	);
};

const EmptyEmbedCode = () => (
	<Inline
		testId="servicedesk-widget-settings.common.embed-code-v2.embed-code-empty-v-2"
		alignBlock="center"
		alignInline="start"
		space="space.100"
		grow="hug"
		xcss={emptyMessageStyles}
	>
		<InfoIcon color={token('color.icon.accent.blue')} label="" />
		<Heading size="xsmall">
			<FormattedMessage {...messages.codeEmptyMessage} />
		</Heading>
	</Inline>
);

const emdedCodeStyles = xcss({
	borderRadius: '3px',
	borderWidth: 'border.width.outline',
	borderColor: 'color.border',
	borderStyle: 'solid',
	color: 'color.text.subtlest',
	fontSize: token('font.body'),
	lineHeight: token('space.250', '20px'),
	padding: 'space.100',
	fontFamily: token('font.family.code'),
});

const buttonStyles = xcss({
	marginTop: 'space.150',
	maxHeight: '32px',
});

const popupStyles = xcss({
	height: 'space.250',
	paddingBlock: 'space.025',
	paddingInline: 'space.075',
	backgroundColor: 'color.background.neutral.bold',
	color: 'color.text.inverse',
	font: token('font.body.UNSAFE_small'),
});

const emptyMessageStyles = xcss({
	marginTop: 'space.150',
});
